/**
 * INBOX STATE (INBOX.md §6) — pure: the Gmail model laid over the derived Inbox (buildInbox). Works for EVERY item kind,
 * current and future — a new source gets read / unread · archive · delete · retention for free.
 *
 *   Inbox    live items not archived / deleted; UNREAD = bold + dot, READ = greyed. The count = unread actionable items.
 *   Archive  archived by you · read items older than AUTO_ARCHIVE_DAYS (automatic) · items you touched whose source is
 *            done (an accepted invite, a cleared alarm) — kept from the stored snapshot; deleted after ARCHIVE_DAYS.
 *   Trash    deleted items; restorable; deleted forever after TRASH_DAYS (or "Empty Trash now").
 *
 * State rows come from GET /data-plane/me/inbox (the person's own, every device): {key, read, readAt, archivedAt,
 * deletedAt, purgedAt, snap}. An item's ``key`` = its id + version (``alarm:x#t3`` — a re-trip is new + unread).
 */

export const RETENTION = { autoArchiveDays: 7, archiveDays: 30, trashDays: 30 };
const DAY = 86400000;

export const itemKey = (it) => String((it && (it.key || it.id)) || '');

/** The bounded display snapshot stored with a state (so Archive / Trash render after the source moves on). */
export function snapOf(it, segment) {
  if (!it) return {};
  const s = { title: it.title || '', detail: it.detail || '', to: it.to || '', kind: it.kind || '',
    segment: segment || it.segment || '' };
  if (it.critical) s.critical = true;
  if (it.fyi) s.fyi = true;
  return s;
}

const ms = (iso) => { const t = Date.parse(iso || ''); return Number.isFinite(t) ? t : 0; };

/** The state after ``op`` — mirrors the server (stepflow_core/inbox_state.update_for) for optimistic updates. PURE. */
export function nextState(prev, op, nowIso, snap) {
  const s = { key: '', read: false, readAt: '', archivedAt: '', deletedAt: '', purgedAt: '', snap: {}, ...(prev || {}) };
  if (snap && Object.keys(snap).length) s.snap = snap;
  s.updatedAt = nowIso;
  if (op === 'read') { s.read = true; s.readAt = nowIso; }
  else if (op === 'unread') { s.read = false; s.readAt = ''; }
  else if (op === 'archive') s.archivedAt = nowIso;
  else if (op === 'unarchive') { s.archivedAt = ''; s.readAt = nowIso; }
  else if (op === 'delete') s.deletedAt = nowIso;
  else if (op === 'restore') { s.deletedAt = ''; s.archivedAt = ''; s.readAt = nowIso; }
  else if (op === 'purge') s.purgedAt = nowIso;
  return s;
}

/** The op that undoes ``op`` (Gmail's "Undo"); '' = not undoable (Delete forever asks first instead). */
export const undoOf = (op) => ({ read: 'unread', unread: 'read', archive: 'unarchive', unarchive: 'archive',
  delete: 'restore', restore: 'delete' }[op] || '');

/**
 * @param {{segments: {action: Array, alerts: Array, overwatch: Array}}} inbox  buildInbox's output (live items)
 * @param {Array|Object} states  state rows (array, or a {key: state} map)
 * @param {number} [now]
 * @returns {{segments, archive: Array, trash: Array, count: number, severity: 'none'|'warn'|'critical',
 *            unread: {action: number, alerts: number, overwatch: number}}}
 */
export function applyInboxState(inbox, states, now = Date.now(), retention = RETENTION) {
  const byKey = new Map();
  for (const s of (Array.isArray(states) ? states : Object.values(states || {}))) {
    if (s && s.key) byKey.set(s.key, s);
  }
  const autoMs = (retention.autoArchiveDays || RETENTION.autoArchiveDays) * DAY;
  const segs = (inbox && inbox.segments) || {};
  const out = { action: [], alerts: [], overwatch: [] };
  const archive = [];
  const trash = [];
  const live = new Set();
  for (const seg of Object.keys(out)) {
    for (const it of segs[seg] || []) {
      const key = itemKey(it);
      live.add(key);
      const st = byKey.get(key);
      const row = { ...it, key, segment: seg, read: !!(st && st.read), readAt: (st && st.readAt) || '' };
      if (st && st.purgedAt) continue;
      if (st && st.deletedAt) { trash.push({ ...row, deletedAt: st.deletedAt }); continue; }
      if (st && st.archivedAt) { archive.push({ ...row, archivedAt: st.archivedAt }); continue; }
      if (row.read && row.readAt && now - ms(row.readAt) > autoMs) {          // read a week ago → Archive, automatically
        archive.push({ ...row, archivedAt: row.readAt, auto: true });
        continue;
      }
      out[seg].push(row);
    }
  }
  // items you touched whose source is DONE (accepted invite, cleared alarm, expired news) → from their snapshot
  for (const [key, st] of byKey) {
    if (live.has(key) || st.purgedAt || !st.snap || !st.snap.title) continue;
    const row = { ...st.snap, id: key, key, read: !!st.read, readAt: st.readAt || '', done: true };
    if (st.deletedAt) trash.push({ ...row, deletedAt: st.deletedAt });
    else archive.push({ ...row, archivedAt: st.archivedAt || st.readAt || st.updatedAt || '' });
  }
  archive.sort((a, b) => ms(b.archivedAt) - ms(a.archivedAt));
  trash.sort((a, b) => ms(b.deletedAt) - ms(a.deletedAt));
  const unreadOf = (xs) => xs.filter((x) => !x.read && !x.fyi).length;
  const unread = { action: unreadOf(out.action), alerts: unreadOf(out.alerts), overwatch: unreadOf(out.overwatch) };
  const count = unread.action + unread.alerts + unread.overwatch;
  const severity = out.alerts.some((x) => x.critical && !x.read) ? 'critical' : count ? 'warn' : 'none';
  return { segments: out, archive, trash, count, severity, unread };
}
